import { Alert, Autocomplete, Box, Grid2, Stack, TextField, Typography } from '@mui/material'
import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react'
import {
  Criteria_Group_Sorting_Config,
  Criteria_Group_Sorting_Config_Bool_Exp,
  Criteria_Sorting_Config,
  Criteria_Sorting_Config_Bool_Exp,
  Query_Root,
} from 'src/@types/graphql'
import { useMessageSource } from 'src/i18n/useMessageSource'
import { fetchProcessCriteriaSorted } from 'src/screens/administration/configuration-management/criteria-configuration/criteriaConfigQueries'
import { useSortCriterias } from 'src/screens/shared/configuration/criteria/CriteriaConfigurationSortingUtils'
import { CriteriaGroupAndCriteriaSortList } from 'src/screens/shared/configuration/criteria/CriteriaGroupAndCriteriaSortList'
import { PROCESS } from 'src/shared/constants/constants'
import { Option } from 'src/shared/form/control'
import { PageLayout } from 'src/shared/layout/PageLayout'
import { ScreenLayout } from 'src/shared/layout/ScreenLayout'
import { useNotificationService } from 'src/shared/utils/NotificationService'
import { useClient } from 'urql'
import {
  getLevelOptions,
  getTranslationForCriteriaType,
  getTranslationForLevel,
} from 'src/screens/administration/configuration-management/ConfigurationUtils'
import { getCriteriaTypeOptions } from 'src/screens/administration/configuration-management/criteria-configuration/CriteriaConfigUtils'
import { debounce } from 'lodash'
import {
  SpecificConfigFilter,
  useFilterSettingsFromLocalStorage,
} from 'src/screens/administration/configuration-management/ConfigurationStorageUtils'

type CriteriaFactsheetFilters = SpecificConfigFilter<'CRITERIA_FACTSHEET'>

export const CriteriaConfigurationFactsheetPage = (): ReactElement => {
  const { getMessage } = useMessageSource()
  const urqlClient = useClient()
  const notificationService = useNotificationService()
  const { switchCriteriaGroupsPosition, switchCriteriasPosition } = useSortCriterias()

  // sort list state
  const [criteriaGroupsSorting, setCriteriaGroupsSorting] = useState<Criteria_Group_Sorting_Config[]>([])
  const [isReorderingLocked, setIsReorderingLocked] = useState(false)

  // filter options
  const criteriaTypeOptions: Option[] = useMemo(() => getCriteriaTypeOptions(getMessage), [getMessage])
  const factsheetLevelsOptions: Option[] = useMemo(() => getLevelOptions(getMessage), [getMessage])

  const defaultCriteriaFactsheetFiltersValues: CriteriaFactsheetFilters = useMemo(
    () => ({
      criteriaFactsheetCriteriaTypeFilter: criteriaTypeOptions[0],
      criteriaFactsheetLevelFilter: factsheetLevelsOptions[0],
    }),
    [criteriaTypeOptions, factsheetLevelsOptions],
  )

  const [criteriaFactsheetFilters, setCriteriaFactsheetFilters] = useFilterSettingsFromLocalStorage(
    defaultCriteriaFactsheetFiltersValues,
  )

  // fetch data
  const fetchCriterias = useCallback(async () => {
    const criteriaGroupWhereClause: Criteria_Group_Sorting_Config_Bool_Exp = {
      _and: [
        {
          criteria_type: {
            _eq: criteriaFactsheetFilters.criteriaFactsheetCriteriaTypeFilter.value as string,
          },
        },
        { process: { _eq: PROCESS.FACTSHEET } },
        { level: { _eq: criteriaFactsheetFilters.criteriaFactsheetLevelFilter.value as string } },
      ],
    }

    const criteriaWhereClause: Criteria_Sorting_Config_Bool_Exp = {
      _and: [
        {
          criteria_type: {
            _eq: criteriaFactsheetFilters.criteriaFactsheetCriteriaTypeFilter.value as string,
          },
        },
        { process: { _eq: PROCESS.FACTSHEET } },
        { level: { _eq: criteriaFactsheetFilters.criteriaFactsheetLevelFilter.value as string } },
      ],
    }

    const { data, error } = await urqlClient
      .query<
        { criteria_group_sorting_config: Query_Root['criteria_group_sorting_config'] },
        {
          criteriaGroupWhereClause: Criteria_Group_Sorting_Config_Bool_Exp
          criteriaWhereClause: Criteria_Sorting_Config_Bool_Exp
        }
      >(fetchProcessCriteriaSorted, {
        criteriaGroupWhereClause: criteriaGroupWhereClause,
        criteriaWhereClause: criteriaWhereClause,
      })
      .toPromise()

    if (!error && data && data.criteria_group_sorting_config) {
      const criteriaGroupsWithCriterias = data.criteria_group_sorting_config.filter(
        (item) => item.criteria_group_config.criteria_sorting_configs.length > 0,
      )
      setCriteriaGroupsSorting(criteriaGroupsWithCriterias)
    } else {
      notificationService.operationFailed()
    }

    setIsReorderingLocked(false)
  }, [
    criteriaFactsheetFilters.criteriaFactsheetCriteriaTypeFilter.value,
    criteriaFactsheetFilters.criteriaFactsheetLevelFilter.value,
    notificationService,
    urqlClient,
  ])

  useEffect(() => {
    fetchCriterias()
  }, [fetchCriterias])

  // handlers
  const onFactsheetLevelChange = useCallback(
    (value: Option) => {
      setCriteriaFactsheetFilters({
        ...criteriaFactsheetFilters,
        criteriaFactsheetLevelFilter: value,
      })
    },
    [criteriaFactsheetFilters, setCriteriaFactsheetFilters],
  )

  const onCriteriaTypeFilterChange = useCallback(
    (newValue: Option) => {
      setCriteriaFactsheetFilters({
        ...criteriaFactsheetFilters,
        criteriaFactsheetCriteriaTypeFilter: newValue,
      })
    },
    [criteriaFactsheetFilters, setCriteriaFactsheetFilters],
  )

  const debouncedHandleReorder = useMemo(
    () =>
      debounce(
        async (reorderFunction: (...args: any[]) => Promise<void>, ...args: any[]) => {
          if (isReorderingLocked) return

          setIsReorderingLocked(true)

          try {
            await reorderFunction(...args)
            await new Promise((resolve) => setTimeout(resolve, 500))
          } catch (e) {
            setIsReorderingLocked(false)
            console.error('Error caught in criteria factsheet sort debounce handler reorder method')
          }
        },
        500,
        { leading: true, trailing: false },
      ),
    [],
  )

  const onSwitchCriteriaGroupPositionInternal = useCallback(
    async (cg1: Criteria_Group_Sorting_Config, cg2: Criteria_Group_Sorting_Config) => {
      try {
        await switchCriteriaGroupsPosition(cg1, cg2, PROCESS.FACTSHEET)
        await fetchCriterias()
      } catch (e) {
        notificationService.operationFailed()
        throw e
      }
    },
    [fetchCriterias, notificationService, switchCriteriaGroupsPosition],
  )

  const onSwitchCriteriaPositionInternal = useCallback(
    async (c1: Criteria_Sorting_Config, c2: Criteria_Sorting_Config) => {
      try {
        await switchCriteriasPosition(c1, c2, PROCESS.FACTSHEET)
        await fetchCriterias()
      } catch (e) {
        notificationService.operationFailed()
        throw e
      }
    },
    [fetchCriterias, notificationService, switchCriteriasPosition],
  )

  const onSwitchCriteriaGroupPosition = useCallback(
    async (cg1: Criteria_Group_Sorting_Config, cg2: Criteria_Group_Sorting_Config) => {
      await debouncedHandleReorder(onSwitchCriteriaGroupPositionInternal, cg1, cg2)
    },
    [debouncedHandleReorder, onSwitchCriteriaGroupPositionInternal],
  )

  const onSwitchCriteriaPosition = useCallback(
    async (c1: Criteria_Sorting_Config, c2: Criteria_Sorting_Config) => {
      await debouncedHandleReorder(onSwitchCriteriaPositionInternal, c1, c2)
    },
    [debouncedHandleReorder, onSwitchCriteriaPositionInternal],
  )

  return (
    <ScreenLayout
      title={`${getMessage('label.navigation.configuration.criteria')} ${getMessage('label.configuration')}`}
    >
      <PageLayout>
        <Stack spacing={2}>
          <Box
            sx={{
              backgroundColor: 'white',
              borderRadius: 1,
              padding: 2,
            }}
          >
            <Stack spacing={2}>
              <Typography variant="body1" color="text.secondary" fontWeight="bold">
                {getMessage(`label.configuration.criteria.preview.settings`)}
              </Typography>
              <Grid2 container spacing={2}>
                <Grid2 size={6}>
                  <Autocomplete
                    options={factsheetLevelsOptions}
                    value={criteriaFactsheetFilters.criteriaFactsheetLevelFilter}
                    onChange={(_, newVal) => onFactsheetLevelChange(newVal)}
                    getOptionLabel={(opt) => getTranslationForLevel(getMessage, opt.value)}
                    disableClearable
                    renderInput={(params) => <TextField {...params} label={getMessage('label.level')} />}
                  />
                </Grid2>
                <Grid2 size={6}>
                  <Autocomplete
                    options={criteriaTypeOptions}
                    value={criteriaFactsheetFilters.criteriaFactsheetCriteriaTypeFilter}
                    onChange={(_, newVal) => onCriteriaTypeFilterChange(newVal)}
                    getOptionLabel={(opt) => getTranslationForCriteriaType(getMessage, opt.value)}
                    disableClearable
                    renderInput={(params) => <TextField {...params} label={getMessage('label.criteria_type')} />}
                  />
                </Grid2>
              </Grid2>
            </Stack>
          </Box>
          <Box>
            <Alert severity="warning">{getMessage('label.warning.config.edit')}</Alert>
          </Box>
          <Stack spacing={5}>
            <CriteriaGroupAndCriteriaSortList
              criteriaGroupsSorting={criteriaGroupsSorting}
              onSwitchCriteriaGroupPosition={onSwitchCriteriaGroupPosition}
              onSwitchCriteriaPosition={onSwitchCriteriaPosition}
              isReorderingLocked={isReorderingLocked}
              processType={PROCESS.FACTSHEET}
            />
          </Stack>
        </Stack>
      </PageLayout>
    </ScreenLayout>
  )
}
